import api from "./axios";

export const searchBusinessApi = async (query: string) => {
	const response = await api.get("/api/businesses/search", {
		params: { q: query },
	});
	return response.data;
};

export const connectBusinessApi = async (payload: Record<string, unknown>) => {
	const response = await api.post("/api/businesses/connect", payload);
	return response.data;
};

export const getConnectedBusinessesApi = async () => {
	const response = await api.get("/api/businesses");
	return response.data;
};

export const getReviewLinkApi = async (businessId: string) => {
	const response = await api.get(`/api/businesses/${businessId}/review-link`);
	return response.data;
};

export const getBusinessReviewsApi = async (businessId: string) => {
	const response = await api.get(`/api/businesses/${businessId}/reviews`);
	return response.data;
};

interface CrisisReviewsOptions {
	level?: string;
	page?: number;
	limit?: number;
}

export const getCrisisReviewsApi = async (
	businessId: string,
	{ level, page = 1, limit = 10 }: CrisisReviewsOptions = {},
) => {
	const response = await api.get(
		`/api/businesses/${businessId}/reviews/crisis`,
		{
			params: {
				businessId,
				...(level ? { level } : {}),
				page,
				limit,
			},
		},
	);
	return response.data;
};

export const getBusinessDetailsApi = async (businessId: string) => {
	const response = await api.get(
		`/api/businesses/${businessId}/reviews/details`,
	);
	return response.data;
};

export const generateResponseApi = async (businessId: string, reviewId: string) => {
	const response = await api.post(
		`/api/businesses/${businessId}/reviews/${reviewId}/generate-response`,
	);
	return response.data;
};

export const getTranslatedReplyApi = async (businessId: string, reviewId: string, locale: string) => {
	const response = await api.get(
		`/api/businesses/${businessId}/reviews/${reviewId}/reply/translate`,
		{ params: { locale } },
	);
	return response.data;
};

export const respondToReviewApi = async (businessId: string, reviewId: string, payload: Record<string, unknown>) => {
	const response = await api.post(
		`/api/businesses/${businessId}/reviews/${reviewId}/respond`,
		payload,
	);
	return response.data;
};

export const syncReviewsApi = async (businessId: string) => {
	const response = await api.post(`/api/businesses/${businessId}/reviews/sync`);
	return response.data;
};

interface RequestSmsPayload {
	customerPhone: string;
	customerName: string;
	messageBody?: string;
}

export const requestSmsApi = async (
	businessId: string,
	{ customerPhone, customerName, messageBody }: RequestSmsPayload,
) => {
	const response = await api.post(
		`/api/businesses/${businessId}/reviews/request-sms`,
		{
			businessId,
			customerPhone: customerPhone.trim(),
			customerName: customerName.trim(),
			messageBody: messageBody?.trim() || "Hi, thank you for your business! Please leave us a review:",
		},
	);
	return response.data;
};

export const removeBusinessApi = async (businessId: string) => {
	const response = await api.delete(`/api/businesses/${businessId}`);
	return response.data;
};

interface SendCampaignPayload {
	emails?: string[];
	subject?: string;
	messageBody?: string;
	fromName?: string;
	fromEmail?: string;
	cc?: string | string[];
	bcc?: string | string[];
	testEmail?: string;
	testOnly?: boolean;
	scheduledAt?: string;
	templateId?: string;
}

export const sendCampaignApi = async (businessId: string, {
	emails,
	subject,
	messageBody,
	fromName,
	fromEmail,
	cc,
	bcc,
	testEmail,
	testOnly,
	scheduledAt,
	templateId,
}: SendCampaignPayload = {}) => {
	const response = await api.post("/api/campaigns", {
		businessId,
		emails: emails || [],
		subject: subject || "",
		messageBody: messageBody || "",
		...(fromName && { fromName }),
		...(fromEmail && { fromEmail }),
		...(cc && { cc: Array.isArray(cc) ? cc : cc.split(",").map(e => e.trim()).filter(e => e) }),
		...(bcc && { bcc: Array.isArray(bcc) ? bcc : bcc.split(",").map(e => e.trim()).filter(e => e) }),
		...(testEmail && { testEmail }),
		...(testOnly !== undefined && { testOnly }),
		...(scheduledAt && { scheduledAt }),
		...(templateId && { templateId }),
	});
	return response.data;
};

export const exportBusinessDataApi = async (businessId: string, { resource, format }: { resource: string; format: string }) => {
	const response = await api.get(`/api/businesses/${businessId}/export`, {
		params: { resource, format },
	});
	return response.data;
};

export const handleOAuthCallbackApi = async ({ code, state }: { code: string; state?: string | null }) => {
	const response = await api.get("/api/businesses/oauth-callback", {
		params: { code, state },
	});
	return response.data;
};

// Add a location to an existing business
export const addLocationToBusinessApi = async (businessId: string, locationData: Record<string, unknown>) => {
	const response = await api.post(`/api/businesses/${businessId}/locations`, locationData);
	return response.data;
};
